// ─── ICS Calendar Export ──────────────────────────────────────────────────────
// Generates an RFC 5545-compliant .ics file and triggers a browser download.
// No dependencies — pure JS.

/**
 * Format a date string (YYYY-MM-DD) as an all-day iCal DATE value.
 * iCal all-day events use DATE not DATE-TIME, so no timezone conversion needed.
 */
function icsDate(isoDate) {
  if (!isoDate) return null
  return isoDate.replace(/-/g, '') // '2025-08-15' → '20250815'
}

/**
 * Format a JS Date as an iCal DATETIME value in UTC.
 */
function icsDateTime(date) {
  return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
}

/**
 * Escape special characters in iCal text values.
 */
function icsText(str) {
  if (!str) return ''
  return String(str)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '')
}

/**
 * Fold long lines per RFC 5545 §3.1 (max 75 octets per line, continuation with CRLF + space).
 */
function foldLine(line) {
  if (line.length <= 75) return line
  const parts = []
  let remaining = line
  parts.push(remaining.slice(0, 75))
  remaining = remaining.slice(75)
  while (remaining.length > 0) {
    parts.push(' ' + remaining.slice(0, 74))
    remaining = remaining.slice(74)
  }
  return parts.join('\r\n')
}

/**
 * Build the iCal content string for a single event.
 *
 * @param {Object} params
 * @param {string}  params.uid          - Unique identifier (e.g. job UUID)
 * @param {string}  params.summary      - Event title
 * @param {string}  params.startDate    - ISO date string 'YYYY-MM-DD'
 * @param {string}  params.endDate      - ISO date string 'YYYY-MM-DD' (end is exclusive in all-day events, so +1 day)
 * @param {string}  [params.location]   - Location/address
 * @param {string}  [params.description]- Description text
 * @param {Date}    [params.created]    - Created timestamp (defaults to now)
 */
function buildICS({ uid, summary, startDate, endDate, location, description, created }) {
  const now = icsDateTime(created || new Date())

  // iCal all-day DTEND is the day AFTER the last day (exclusive)
  // e.g. if teardown is Aug 17, DTEND should be Aug 18
  let dtend = endDate || startDate
  if (dtend) {
    const d = new Date(dtend + 'T12:00:00')
    d.setDate(d.getDate() + 1)
    dtend = icsDate(d.toISOString().slice(0, 10))
  }

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Winnipeg Tent Rental//WTR CRM//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}@wtr-crm`,
    `DTSTAMP:${now}`,
    `DTSTART;VALUE=DATE:${icsDate(startDate)}`,
    `DTEND;VALUE=DATE:${dtend}`,
    foldLine(`SUMMARY:${icsText(summary)}`),
    location  ? foldLine(`LOCATION:${icsText(location)}`)    : null,
    description ? foldLine(`DESCRIPTION:${icsText(description)}`) : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean)

  return lines.join('\r\n')
}

/**
 * Build a safe filename from a string (removes special chars).
 */
function safeFilename(str) {
  return (str || 'event').replace(/[^a-z0-9\-_ ]/gi, '').replace(/\s+/g, '-').toLowerCase()
}

/**
 * Download an .ics file for a WTR job.
 *
 * @param {Object} job - job record from Supabase
 *   job.id, job.event_date, job.setup_date, job.teardown_date,
 *   job.event_type, job.address, job.job_notes, job.estimated_setup_hours,
 *   job.estimated_teardown_hours, job.crew_required
 */
export function downloadJobICS(job) {
  const eventType = job.event_type
    ? job.event_type.charAt(0).toUpperCase() + job.event_type.slice(1)
    : 'Job'

  const summary = `WTR – ${eventType}${job.address ? ` @ ${job.address.split(',')[0]}` : ''}`

  // Use setup_date as start if available, otherwise event_date
  const startDate = job.setup_date || job.event_date
  // Use teardown_date as end if available, otherwise event_date
  const endDate = job.teardown_date || job.event_date

  // Build a useful description
  const descParts = []
  if (job.event_date && job.event_date !== startDate) {
    descParts.push(`Event Date: ${job.event_date}`)
  }
  if (job.setup_date) descParts.push(`Setup: ${job.setup_date}`)
  if (job.teardown_date) descParts.push(`Teardown: ${job.teardown_date}`)
  if (job.estimated_setup_hours)   descParts.push(`Setup hours: ~${job.estimated_setup_hours} hrs`)
  if (job.estimated_teardown_hours) descParts.push(`Teardown hours: ~${job.estimated_teardown_hours} hrs`)
  if (job.crew_required) descParts.push(`Crew: ${job.crew_required} people`)
  if (job.job_notes) descParts.push(`\nNotes:\n${job.job_notes}`)

  const icsContent = buildICS({
    uid:         job.id,
    summary,
    startDate,
    endDate,
    location:    job.address || '',
    description: descParts.join('\n'),
  })

  // Trigger download
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = `wtr-${safeFilename(eventType)}-${job.event_date || 'job'}.ics`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
